import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Award, Download, ScrollText, Scale, Sparkles, Handshake, GitBranch, GitCommit, Users } from "lucide-react";
import { Button, Card, EmptyState, Label, PageHeader, Section, Tag } from "../components/ui";
import { BalancePanel } from "./GovernanceBalancePage";
import { buildFirmContribution, buildParticipationLeaderboard } from "../lib/participation";
import { buildCooperationReputation } from "../lib/reputation";
import { buildTransparencyRegister, type TransparencyKind } from "../lib/transparency";
import { buildDecisionRegisterReport, downloadCsv } from "../lib/reports";
import { affiliationViewerRoles, fullMemberRoles } from "../lib/domain";
import type { LexData } from "../lib/types";

type GovernanceTab = "recognition" | "transparency" | "balance";

const kindLabel: Record<TransparencyKind, string> = { decision: "Decision", mandate: "Mandate", outreach: "Outreach", cooperation: "Cooperation", admission: "Admission" };

export function GovernancePage({ data }: { data: LexData; mode?: "convex" | "demo" }) {
  const [params, setParams] = useSearchParams();
  const canSeeBalance = [...affiliationViewerRoles, ...fullMemberRoles].includes(data.viewer.role);
  const requestedTab = (params.get("tab") as GovernanceTab | null) ?? "recognition";
  const tab: GovernanceTab = requestedTab === "balance" && !canSeeBalance ? "recognition" : requestedTab;

  const tabs: { id: GovernanceTab; label: string; icon: typeof Award }[] = [
    { id: "recognition", label: "Recognition", icon: Award },
    { id: "transparency", label: "Transparency register", icon: ScrollText },
    ...(canSeeBalance ? [{ id: "balance" as const, label: "Balance & capture", icon: Scale }] : []),
  ];

  return (
    <>
      <PageHeader
        title="Governance"
        subtitle="Participation made visible, decisions on the record, and the network kept balanced. Recognition rewards contribution; cooperation builds trust but never voting power; the balance monitor keeps any one firm, jurisdiction or group from dominating."
      />
      <div className="mb-5 flex flex-wrap gap-2">
        {tabs.map((item) => {
          const active = item.id === tab;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setParams({ tab: item.id }, { replace: true })}
              className={`inline-flex min-h-10 items-center gap-2 rounded-lg border px-3 text-sm font-semibold ${active ? "border-primary bg-primary text-white" : "border-line bg-surface text-secondary hover:bg-hover"}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </button>
          );
        })}
      </div>

      {tab === "recognition" ? <RecognitionPanel data={data} /> : null}
      {tab === "transparency" ? <TransparencyPanel data={data} /> : null}
      {tab === "balance" && canSeeBalance ? <BalancePanel data={data} /> : null}
    </>
  );
}

function RecognitionPanel({ data }: { data: LexData }) {
  const viewerFirm = data.viewerFirm;
  const myContribution = useMemo(() => (viewerFirm ? buildFirmContribution(data, viewerFirm.id) : null), [data, viewerFirm]);
  const myReputation = useMemo(() => (viewerFirm ? buildCooperationReputation(data, viewerFirm.id) : null), [data, viewerFirm]);
  const leaderboard = useMemo(() => buildParticipationLeaderboard(data), [data]);

  return (
    <>
      {myContribution ? (
        <Section title="Your firm's recognition" kicker={viewerFirm?.name ?? "Your firm"}>
          <div className="grid gap-3 lg:grid-cols-3">
            <Card>
              <Label>Current voting weight</Label>
              <div className="mt-1 text-2xl font-semibold text-ink">{myContribution.totalWeight.toFixed(2)}</div>
              <div className="mt-1 text-[12px] text-secondary">
                Base {myContribution.baseWeight.toFixed(2)} + participation {myContribution.participationBonus.toFixed(2)} (capped at {myContribution.maxTotalWeight.toFixed(2)}).
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#EDEFF3]">
                <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, myContribution.capUsedPct * 100)}%` }} />
              </div>
              <div className="mt-1 text-[11px] text-muted">{(myContribution.capUsedPct * 100).toFixed(0)}% of your participation bonus earned. Active members matter more — but nobody becomes the monarch of the network.</div>
            </Card>
            <Card className="lg:col-span-2">
              <Label>How it was earned</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {myContribution.breakdown.length > 0 ? (
                  myContribution.breakdown.map((item) => (
                    <div key={item.category} className="flex items-center justify-between rounded-lg border border-line px-3 py-2">
                      <span className="text-[13px] text-secondary">{item.label}</span>
                      <span className="text-sm font-semibold text-ink">{item.count}</span>
                    </div>
                  ))
                ) : (
                  <EmptyState>No recorded contribution yet this period. Start a discussion, comment on a proposal or join a drafting group.</EmptyState>
                )}
              </div>
            </Card>
          </div>
          {myReputation ? (
            <Card className="mt-3 border-emerald-200 bg-emerald-50/30">
              <div className="flex items-start gap-3">
                <Handshake className="mt-0.5 h-4 w-4 text-emerald-700" />
                <div>
                  <div className="text-sm font-semibold text-ink">Trusted cooperation — {myReputation.responsivenessLabel}</div>
                  <p className="mt-1 text-[13px] leading-5 text-secondary">
                    {myReputation.completedCooperations} cooperation{myReputation.completedCooperations === 1 ? "" : "s"} completed · {myReputation.debriefsSubmitted} debrief{myReputation.debriefsSubmitted === 1 ? "" : "s"} shared
                    {myReputation.averageRating !== null ? ` · ${myReputation.averageRating.toFixed(1)}★ average` : ""}.
                  </p>
                  <p className="mt-1 text-[11px] text-muted">Cooperation builds reputation and trust — it does not affect votes or voting weight.</p>
                  {myReputation.trustSignals.length > 0 ? <div className="mt-2 flex flex-wrap gap-1.5">{myReputation.trustSignals.map((signal) => <Tag key={signal}>{signal}</Tag>)}</div> : null}
                </div>
              </div>
            </Card>
          ) : null}
        </Section>
      ) : null}

      <Section title="Network participation" kicker="Within the participation cap" aside={<span className="mono text-[11px] text-muted">{leaderboard.length} contributing firms</span>}>
        <Card className="p-0">
          <div className="divide-y divide-line">
            {leaderboard.map((firm, index) => (
              <div key={firm.firmId} className="flex items-center gap-3 px-4 py-3">
                <span className="mono w-6 shrink-0 text-center text-sm text-muted">{index + 1}</span>
                <Link to={`/members/${firm.firmId}`} className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-ink hover:underline">{firm.firmName}</div>
                  <div className="text-[12px] text-secondary">
                    {firm.jurisdiction} · {firm.votesCast} votes · {firm.comments} contributions · {firm.amendmentsSponsored} amendments
                  </div>
                </Link>
                <div className="w-24 shrink-0 text-right">
                  <div className="text-sm font-semibold text-ink">{firm.totalWeight.toFixed(2)}</div>
                  <div className="text-[11px] text-secondary">weight</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted">
          <Sparkles className="h-3.5 w-3.5" /> Participation raises influence only within a capped bonus; cooperation and referrals are deliberately excluded.
        </p>
      </Section>
    </>
  );
}

function TransparencyPanel({ data }: { data: LexData }) {
  const register = useMemo(() => buildTransparencyRegister(data), [data]);

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={() => downloadCsv(buildDecisionRegisterReport(data))}>
          <span className="inline-flex items-center gap-2"><Download className="h-4 w-4" /> Decision register (CSV)</span>
        </Button>
      </div>
      <Section title="At a glance" kicker="Institutional memory">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <MiniStat label="Adopted" value={register.decisionsAdopted} />
          <MiniStat label="Rejected" value={register.decisionsRejected} />
          <MiniStat label="Mandates" value={register.mandatesApproved} />
          <MiniStat label="Outreach" value={register.outreachLogged} />
          <MiniStat label="Cooperations" value={register.cooperationsRecorded} />
          <MiniStat label="Admissions" value={register.admissions} />
        </div>
      </Section>
      <ProjectRegistry register={register} />
      <Section title="Register" kicker="Decisions · mandates · outreach · cooperation · admissions">
        {register.entries.length > 0 ? (
          <Card className="p-0">
            <div className="divide-y divide-line">
              {register.entries.map((entry) => {
                const body = (
                  <div className="flex items-start gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Tag>{kindLabel[entry.kind]}</Tag>
                        <span className="truncate text-sm font-semibold text-ink">{entry.title}</span>
                      </div>
                      <div className="mt-0.5 text-[12px] text-secondary">{entry.detail}</div>
                    </div>
                    <div className="shrink-0 text-right">
                      <Tag>{entry.outcome}</Tag>
                      <div className="mt-1 text-[11px] text-muted">{new Date(entry.occurredAt).toLocaleDateString()}</div>
                    </div>
                  </div>
                );
                return entry.href ? (
                  <Link key={entry.id} to={entry.href} className="block hover:bg-hover">
                    {body}
                  </Link>
                ) : (
                  <div key={entry.id}>{body}</div>
                );
              })}
            </div>
          </Card>
        ) : (
          <EmptyState>No certified decisions, approved mandates or recorded cooperation yet. The register fills as the network acts.</EmptyState>
        )}
        <p className="mt-2 text-[11px] text-muted">No client names, matter values or fees are ever recorded here.</p>
      </Section>
    </>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <div className="text-2xl font-semibold text-ink">{value}</div>
      <div className="mt-1 text-[12px] text-secondary">{label}</div>
    </Card>
  );
}

function ProjectRegistry({ register }: { register: ReturnType<typeof buildTransparencyRegister> }) {
  return (
    <Section title="Project registry" kicker="Repositories · proposals · Charters">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-canvas p-4">
        <div><div className="flex items-center gap-2 font-semibold text-ink"><GitBranch className="h-4 w-4 text-primary" /> Active project capacity</div><p className="mt-1 text-sm text-secondary">At most half as many active projects as member firms. Amendments are unlimited.</p></div>
        <Tag>{register.activeProjectCount} / {register.activeProjectLimit} active</Tag>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {register.projects.map((project) => (
          <Card key={project.id} className="overflow-hidden p-0">
            <Link to={project.href} className="block border-b border-line p-4 hover:bg-hover">
              <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="flex items-center gap-2"><GitBranch className="h-4 w-4 text-primary" /><span className="truncate font-semibold text-ink">{project.title}</span></div><p className="mt-2 line-clamp-2 text-sm leading-6 text-secondary">{project.summary}</p></div><Tag>{project.status === "charter" ? "Charter" : project.status === "declined" ? "Declined" : "Active project"}</Tag></div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted"><span>Deadline {new Date(project.deadline).toLocaleDateString()}</span><span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {project.patronFirms.length} patrons</span><span className="inline-flex items-center gap-1"><GitCommit className="h-3.5 w-3.5" /> {project.commits.length} amendments</span></div>
            </Link>
            <div className="divide-y divide-line">
              {project.commits.length > 0 ? project.commits.map((commit) => <div key={commit.id} className="flex items-start gap-3 px-4 py-3"><GitCommit className="mt-0.5 h-4 w-4 shrink-0 text-secondary" /><div className="min-w-0 flex-1"><div className="text-sm font-medium text-ink">{commit.label}</div><div className="mt-0.5 truncate text-xs text-secondary">{commit.summary}</div><div className="mt-1 text-[11px] text-muted">{commit.authorFirm} · {new Date(commit.occurredAt).toLocaleDateString()}</div></div><Tag>{commit.status === "merged" ? "Merged proposal" : commit.status === "declined" ? "Declined proposal" : "Under review"}</Tag></div>) : <div className="px-4 py-3 text-sm text-muted">No amendments committed yet.</div>}
            </div>
          </Card>
        ))}
      </div>
    </Section>
  );
}